/* DRIVE IQ — ADVENTURE MODE V600 STATE
   File: adventure-state.js

   Purpose:
   - One shared state authority for Adventure Mode V600.
   - Replaces scattered global variables.
   - No Mapbox creation.
   - No DOM rendering.
   - No routing.
   - No scan execution.

   Load after:
   1. adventure-config.js
   2. adventure-utils.js
*/

(function (window) {
  "use strict";

  if (window.DriveIQAdventureState) {
    return;
  }

  var config = window.DriveIQAdventureConfig;
  var utils = window.DriveIQAdventureUtils;

  if (!config) {
    throw new Error(
      "DriveIQAdventureState requires adventure-config.js to load first."
    );
  }

  if (!utils) {
    throw new Error(
      "DriveIQAdventureState requires adventure-utils.js to load first."
    );
  }

  function cloneValue(value) {
    var key;
    var output;

    if (value === null || typeof value !== "object") {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(cloneValue);
    }

    output = {};

    for (key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        output[key] = cloneValue(value[key]);
      }
    }

    return output;
  }

  function createInitialState() {
    return {
      build: "V600_STATE_FOUNDATION",
      initialized: false,
      bootedAt: 0,
      updatedAt: 0,

      map: {
        instance: null,
        loaded: false,
        styleLoaded: false,
        interactionEnabled: false,
        containerReady: false,
        cameraMoving: false,
        userInteracting: false,
        lastResizeAt: 0,
        lastCameraUpdateAt: 0,
        camera: {
          center: null,
          zoom: config.map.initialZoom,
          bearing: config.map.defaultBearing,
          pitch: config.map.defaultPitch
        }
      },

      location: {
        lastFix: null,
        permission: "unknown",
        watching: false,
        accuracyMeters: null,
        updatedAt: 0
      },

      destination: {
        selected: null,
        committed: null,
        locked: false,
        lockReason: "",
        selectedAt: 0,
        committedAt: 0
      },

      scan: {
        active: false,
        token: "",
        category: config.scan.defaultCategory,
        scope: config.scan.defaultScope,
        center: null,
        radiusMeters: config.scan.defaultRadiusMeters,
        fallbackPass: 0,
        startedAt: 0,
        completedAt: 0,
        status: "idle",
        error: null
      },

      results: {
        items: [],
        preferred: null,
        count: 0,
        committedToken: "",
        committedAt: 0
      },

      selection: {
        pinKey: "",
        selectedPlace: null,
        selectedAt: 0,
        source: ""
      },

      placeCard: {
        open: false,
        item: null,
        itemKey: "",
        openedAt: 0,
        photoHydrating: false,
        routeRequested: false
      },

      route: {
        active: false,
        requested: false,
        selectedPlace: null,
        payload: null,
        geometry: null,
        requestedAt: 0,
        committedAt: 0,
        launchStartedAt: 0,
        error: null
      },

      ui: {
        mode: "menu",
        menuOpen: true,
        searchOpen: false,
        scanningVisible: false,
        mapVisible: false,
        placeCardVisible: false,
        legacyShelfVisible: false,
        toastMessage: "",
        lastModeChangedAt: 0
      },

      resume: {
        restoring: false,
        restored: false,
        snapshot: null,
        backgroundedAt: 0,
        foregroundedAt: 0,
        restoredAt: 0
      },

      diagnostics: {
        lastAction: "",
        lastEvent: "",
        lastError: null,
        eventCount: 0,
        mutationCount: 0
      }
    };
  }

  var state = createInitialState();
  var listeners = {};
  var anyListeners = [];
  var transactionDepth = 0;
  var queuedNotifications = [];

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function pathParts(path) {
    if (Array.isArray(path)) {
      return path.slice(0);
    }

    return String(path || "")
      .split(".")
      .filter(function (part) {
        return part !== "";
      });
  }

  function getAtPath(path) {
    var parts = pathParts(path);
    var cursor = state;
    var index;

    if (!parts.length) {
      return state;
    }

    for (index = 0; index < parts.length; index += 1) {
      if (
        cursor === null ||
        typeof cursor !== "object" ||
        !Object.prototype.hasOwnProperty.call(cursor, parts[index])
      ) {
        return undefined;
      }

      cursor = cursor[parts[index]];
    }

    return cursor;
  }

  function setAtPath(path, value) {
    var parts = pathParts(path);
    var cursor = state;
    var index;
    var finalKey;

    if (!parts.length) {
      throw new Error("DriveIQAdventureState.set requires a state path.");
    }

    for (index = 0; index < parts.length - 1; index += 1) {
      if (
        cursor[parts[index]] === null ||
        typeof cursor[parts[index]] !== "object"
      ) {
        cursor[parts[index]] = {};
      }

      cursor = cursor[parts[index]];
    }

    finalKey = parts[parts.length - 1];
    cursor[finalKey] = value;
  }

  function notify(path, value, previousValue, metadata) {
    var payload = {
      path: path,
      value: value,
      previousValue: previousValue,
      metadata: metadata || {},
      timestamp: now()
    };

    if (transactionDepth > 0) {
      queuedNotifications.push(payload);
      return;
    }

    dispatchNotification(payload);
  }

  function dispatchNotification(payload) {
    var pathList = listeners[payload.path] || [];
    var index;

    for (index = 0; index < pathList.length; index += 1) {
      try {
        pathList[index](payload);
      } catch (error) {
        recordError(error, "listener:" + payload.path);
      }
    }

    for (index = 0; index < anyListeners.length; index += 1) {
      try {
        anyListeners[index](payload);
      } catch (errorAny) {
        recordError(errorAny, "listener:any");
      }
    }
  }

  function flushNotifications() {
    var pending = queuedNotifications.slice(0);
    var index;

    queuedNotifications.length = 0;

    for (index = 0; index < pending.length; index += 1) {
      dispatchNotification(pending[index]);
    }
  }

  function recordError(error, source) {
    state.diagnostics.lastError = {
      source: String(source || "unknown"),
      message: String(
        error && error.message ? error.message : error || "Unknown error"
      ),
      timestamp: now()
    };

    if (window.console && window.console.error) {
      window.console.error(
        config.logging.prefix,
        "State error",
        state.diagnostics.lastError
      );
    }
  }

  function set(path, value, metadata) {
    var previousValue = getAtPath(path);

    setAtPath(path, value);

    state.updatedAt = now();
    state.diagnostics.mutationCount += 1;
    state.diagnostics.lastAction = String(
      metadata && metadata.action
        ? metadata.action
        : "set:" + path
    );

    notify(path, value, previousValue, metadata);

    return value;
  }

  function update(path, updater, metadata) {
    var currentValue = getAtPath(path);
    var nextValue;

    if (typeof updater !== "function") {
      throw new Error(
        "DriveIQAdventureState.update requires an updater function."
      );
    }

    nextValue = updater(currentValue);

    return set(path, nextValue, metadata);
  }

  function merge(path, partial, metadata) {
    var currentValue = getAtPath(path);
    var output = {};
    var key;

    if (
      currentValue &&
      typeof currentValue === "object" &&
      !Array.isArray(currentValue)
    ) {
      for (key in currentValue) {
        if (Object.prototype.hasOwnProperty.call(currentValue, key)) {
          output[key] = currentValue[key];
        }
      }
    }

    if (partial && typeof partial === "object") {
      for (key in partial) {
        if (Object.prototype.hasOwnProperty.call(partial, key)) {
          output[key] = partial[key];
        }
      }
    }

    return set(path, output, metadata);
  }

  function get(path, fallback) {
    var value = getAtPath(path);

    if (typeof value === "undefined") {
      return fallback;
    }

    return value;
  }

  function snapshot() {
    var copy = cloneValue(state);

    copy.map.instance = null;

    return copy;
  }

  function reset(section) {
    var fresh = createInitialState();
    var previousState;

    if (!section) {
      previousState = state;
      state = fresh;
      notify(
        "",
        state,
        previousState,
        { action: "reset:all" }
      );
      return state;
    }

    if (!Object.prototype.hasOwnProperty.call(fresh, section)) {
      throw new Error(
        "Unknown Drive IQ Adventure state section: " + section
      );
    }

    return set(
      section,
      fresh[section],
      { action: "reset:" + section }
    );
  }

  function subscribe(path, handler) {
    var normalizedPath = String(path || "");

    if (typeof handler !== "function") {
      throw new Error(
        "DriveIQAdventureState.subscribe requires a handler function."
      );
    }

    if (!listeners[normalizedPath]) {
      listeners[normalizedPath] = [];
    }

    listeners[normalizedPath].push(handler);

    return function unsubscribe() {
      var list = listeners[normalizedPath] || [];
      var index = list.indexOf(handler);

      if (index >= 0) {
        list.splice(index, 1);
      }
    };
  }

  function subscribeAny(handler) {
    if (typeof handler !== "function") {
      throw new Error(
        "DriveIQAdventureState.subscribeAny requires a handler function."
      );
    }

    anyListeners.push(handler);

    return function unsubscribeAny() {
      var index = anyListeners.indexOf(handler);

      if (index >= 0) {
        anyListeners.splice(index, 1);
      }
    };
  }

  function transaction(action, callback) {
    var result;

    if (typeof callback !== "function") {
      throw new Error(
        "DriveIQAdventureState.transaction requires a callback."
      );
    }

    transactionDepth += 1;

    try {
      result = callback(api);
      state.diagnostics.lastAction = String(
        action || "state-transaction"
      );
    } catch (error) {
      recordError(error, action || "state-transaction");
      throw error;
    } finally {
      transactionDepth -= 1;

      if (transactionDepth <= 0) {
        transactionDepth = 0;
        flushNotifications();
      }
    }

    return result;
  }

  function createScanToken() {
    return utils.uuid();
  }

  function beginScan(options) {
    var next = options || {};
    var token = String(next.token || createScanToken());

    transaction("scan:begin", function () {
      merge("scan", {
        active: true,
        token: token,
        category: String(
          next.category || state.scan.category || config.scan.defaultCategory
        ),
        scope: String(
          next.scope || state.scan.scope || config.scan.defaultScope
        ),
        center: next.center || null,
        radiusMeters: Number(
          next.radiusMeters || config.scan.defaultRadiusMeters
        ),
        fallbackPass: 0,
        startedAt: now(),
        completedAt: 0,
        status: "scanning",
        error: null
      });

      merge("results", {
        items: [],
        preferred: null,
        count: 0,
        committedToken: "",
        committedAt: 0
      });

      merge("selection", {
        pinKey: "",
        selectedPlace: null,
        selectedAt: 0,
        source: ""
      });

      merge("placeCard", {
        open: false,
        item: null,
        itemKey: "",
        openedAt: 0,
        photoHydrating: false,
        routeRequested: false
      });

      merge("ui", {
        mode: "scanning",
        menuOpen: false,
        scanningVisible: true,
        mapVisible: true,
        placeCardVisible: false,
        legacyShelfVisible: false,
        lastModeChangedAt: now()
      });
    });

    return token;
  }

  function commitScanResults(token, items, preferred) {
    var activeToken = String(state.scan.token || "");
    var incomingToken = String(token || "");
    var committedItems;

    if (!incomingToken || incomingToken !== activeToken) {
      return false;
    }

    committedItems = Array.isArray(items) ? items.slice(0) : [];

    transaction("scan:commit", function () {
      merge("results", {
        items: committedItems,
        preferred: preferred || null,
        count: committedItems.length,
        committedToken: incomingToken,
        committedAt: now()
      });

      merge("scan", {
        active: false,
        completedAt: now(),
        status: "complete",
        error: null
      });

      merge("ui", {
        mode: "map",
        menuOpen: false,
        scanningVisible: false,
        mapVisible: true,
        placeCardVisible: false,
        legacyShelfVisible: false,
        lastModeChangedAt: now()
      });
    });

    return true;
  }

  function failScan(token, error) {
    if (String(token || "") !== String(state.scan.token || "")) {
      return false;
    }

    transaction("scan:fail", function () {
      merge("scan", {
        active: false,
        completedAt: now(),
        status: "error",
        error: String(
          error && error.message ? error.message : error || "Scan failed"
        )
      });

      merge("ui", {
        mode: "menu",
        menuOpen: true,
        scanningVisible: false,
        mapVisible: true,
        placeCardVisible: false,
        legacyShelfVisible: false,
        lastModeChangedAt: now()
      });
    });

    return true;
  }

  function selectPlace(item, source) {
    var key = "";
    var selectedAt = now();

    if (item) {
      key = String(
        item.placeId ||
        item.place_id ||
        item.googlePlaceId ||
        item.id ||
        item.label ||
        item.name ||
        ""
      ) + "|" +
      String(item.lat || "") + "|" +
      String(item.lng || "");
    }

    transaction("place:select", function () {
      merge("selection", {
        pinKey: key,
        selectedPlace: item || null,
        selectedAt: selectedAt,
        source: String(source || "unknown")
      });

      merge("placeCard", {
        open: !!item,
        item: item || null,
        itemKey: key,
        openedAt: item ? selectedAt : 0,
        photoHydrating: false,
        routeRequested: false
      });

      merge("ui", {
        mode: item ? "place" : "map",
        menuOpen: false,
        scanningVisible: false,
        mapVisible: true,
        placeCardVisible: !!item,
        legacyShelfVisible: false,
        lastModeChangedAt: selectedAt
      });
    });

    return item || null;
  }

  function closePlace() {
    transaction("place:close", function () {
      merge("placeCard", {
        open: false,
        item: null,
        itemKey: "",
        openedAt: 0,
        photoHydrating: false,
        routeRequested: false
      });

      merge("ui", {
        mode: "map",
        menuOpen: false,
        scanningVisible: false,
        mapVisible: true,
        placeCardVisible: false,
        legacyShelfVisible: false,
        lastModeChangedAt: now()
      });
    });
  }

  function setDestination(item, reason, committed) {
    var timestamp = now();

    transaction("destination:set", function () {
      merge("destination", {
        selected: item || null,
        committed: committed === true ? item || null : state.destination.committed,
        locked: committed === true,
        lockReason: String(reason || ""),
        selectedAt: item ? timestamp : 0,
        committedAt: committed === true && item ? timestamp : state.destination.committedAt
      });
    });

    return item || null;
  }

  function requestRoute(item) {
    var selected = item || state.placeCard.item || state.selection.selectedPlace;

    if (!selected) {
      return false;
    }

    transaction("route:request", function () {
      merge("placeCard", {
        routeRequested: true
      });

      merge("route", {
        active: false,
        requested: true,
        selectedPlace: selected,
        requestedAt: now(),
        error: null
      });
    });

    return true;
  }

  function setUIState(mode) {
    var normalized = String(mode || "menu");
    var patch = {
      mode: normalized,
      menuOpen: normalized === "menu",
      scanningVisible: normalized === "scanning",
      mapVisible:
        normalized === "map" ||
        normalized === "place" ||
        normalized === "scanning",
      placeCardVisible: normalized === "place",
      legacyShelfVisible: false,
      lastModeChangedAt: now()
    };

    return merge(
      "ui",
      patch,
      { action: "ui:mode:" + normalized }
    );
  }

  function initialize() {
    if (state.initialized) {
      return snapshot();
    }

    state.initialized = true;
    state.bootedAt = now();
    state.updatedAt = state.bootedAt;
    state.ui.lastModeChangedAt = state.bootedAt;

    return snapshot();
  }

  var api = {
    initialize: initialize,
    get: get,
    set: set,
    update: update,
    merge: merge,
    snapshot: snapshot,
    reset: reset,
    subscribe: subscribe,
    subscribeAny: subscribeAny,
    transaction: transaction,
    createScanToken: createScanToken,
    beginScan: beginScan,
    commitScanResults: commitScanResults,
    failScan: failScan,
    selectPlace: selectPlace,
    closePlace: closePlace,
    setDestination: setDestination,
    requestRoute: requestRoute,
    setUIState: setUIState
  };

  window.DriveIQAdventureState = api;

  initialize();

  if (
    config.logging.enabled &&
    window.console &&
    window.console.log
  ) {
    window.console.log(
      config.logging.prefix,
      "State loaded",
      state.build
    );
  }
}(window));
